import { FormDataAPIType } from 'components/common/form';
import React, { useRef, useEffect, useMemo } from 'react';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import BlockEditPanelControls from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelControls';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { Box, FormLabel, Stack } from '@mui/material';
import BlockEditPanelHeader from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelHeader';
import { DatasetFieldValueType, RendererFilter, RendererFilterType } from '@report-content';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { FormComponentValue } from 'components/common/form/layout/control';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { AssocArray } from 'tools/types';

interface ISortOrderDirections {
    order: 'ASC' | 'DESC';
    label: string;
    valueType: DatasetFieldValueType;
}

export const sortOrderDirections: Array<ISortOrderDirections> = [
    { order: 'ASC', label: 'label_a_to_z', valueType: 'text' },
    { order: 'DESC', label: 'label_z_to_a', valueType: 'text' },
    { order: 'ASC', label: 'label_ascending', valueType: 'numeric' },
    { order: 'DESC', label: 'label_descending', valueType: 'numeric' },
    { order: 'ASC', label: 'label_ascending', valueType: 'datetime' },
    { order: 'DESC', label: 'label_descending', valueType: 'datetime' },
];

interface IFilterType {
    type: RendererFilterType;
    label: string;
    valueType: DatasetFieldValueType;
}
const filterTypes: Array<IFilterType> = [
    { type: 'dropdown', label: 'label_dropdown', valueType: 'text' },
    { type: 'multiselect', label: 'label_multiselect', valueType: 'text' },
    { type: 'dropdown', label: 'label_dropdown', valueType: 'numeric' },
    { type: 'multiselect', label: 'label_multiselect', valueType: 'numeric' },
    { type: 'range', label: 'label_range', valueType: 'numeric' },
    { type: 'dropdown', label: 'label_dropdown', valueType: 'datetime' },
    { type: 'multiselect', label: 'label_multiselect', valueType: 'datetime' },
    { type: 'range', label: 'label_range', valueType: 'datetime' },
    { type: 'charting_interval', label: 'label_charting_interval', valueType: 'datetime' },
];

export default function FieldForm({
    field,
    onApply,
    onCancel,
    title,
    elementId,
}: {
    field: RendererFilter;
    onApply: (field: RendererFilter) => void;
    onCancel: () => void;
    title?: string;
    elementId: number;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const elementProps: FormDataAPIType = useCustomSimplifiedForm({
        filter_label: field.filter_label,
        include_all_value: field.include_all_value ?? 'Y',
        show_date_only: field.show_date_only ?? 'N',
        filterType: field.filterType ?? 'dropdown',
        sortDirection: field.sortDirection,
        use_external_filter_value: field.use_external_filter_value ?? 'N',
        external_dataset_id: field.external_dataset_id ? String(field.external_dataset_id) : '',
        external_dataset_column_value_id: field.external_dataset_column_value_id ?? '',
        external_dataset_column_value_label: field.external_dataset_column_value_label ?? '',
        external_dataset_column_title_id: field.external_dataset_column_title_id ?? '',
        external_dataset_column_title_label: field.external_dataset_column_title_label ?? '',
        external_delimiter: field.external_delimiter ?? '~',
    });

    const fieldType: RendererFilterType = elementProps.form.hookFormWatch('filterType');
    const valueType = field.value_type;
    const useExternalFilterValue = elementProps.form.hookFormWatch('use_external_filter_value');
    const selectedDatasetId = elementProps.form.hookFormWatch('external_dataset_id');

    const { data: datasetsList = [], isLoading: isDatasetListLoading } = useQuery<FormComponentValue[]>({
        queryKey: ['manage-filters', 'external-datasets'],
        queryFn: async () => {
            const response = await instance.get(`data/editor/report/${elementId}/get-datasets`);
            return response.data?.data ?? [];
        },
        enabled: useExternalFilterValue === 'Y',
    });

    const selectedDataset = useMemo(() => {
        if (!datasetsList || !selectedDatasetId) return null;
        return datasetsList.find((d) => d.value == selectedDatasetId) || null;
    }, [selectedDatasetId, datasetsList]);

    const { data: columnsList = [], isLoading: isColumnsListLoading } = useQuery<FormComponentValue[]>({
        queryKey: ['manage-filters', 'dataset-columns', selectedDatasetId],
        queryFn: async () => {
            let result: AssocArray<string> = {};
            if (selectedDataset?.props?.can_view == 'Y') {
                const response = await instance.get(`/data/editor/dataset/${selectedDatasetId}/columns`);
                result = response.data ?? [];
            } else {
                if (field.external_dataset_column_value_id) {
                    result[field.external_dataset_column_value_id] = field.external_dataset_column_value_label ?? '';
                }
                if (field.external_dataset_column_title_id) {
                    result[field.external_dataset_column_title_id] = field.external_dataset_column_title_label ?? '';
                }
            }

            return [{ value: '', label: '--' }].concat(
                Object.entries(result).map(
                    ([value, label]): FormComponentValue => ({
                        value: String(value),
                        label: label,
                    }),
                ),
            );
        },
        enabled: useExternalFilterValue === 'Y' && selectedDataset != null,
    });

    const prevDatasetIdRef = useRef(selectedDatasetId);
    useEffect(() => {
        if (prevDatasetIdRef.current !== selectedDatasetId) {
            prevDatasetIdRef.current = selectedDatasetId;
            elementProps.form.hookFormSetValue('external_dataset_column_value_id', '');
            elementProps.form.hookFormSetValue('external_dataset_column_title_id', '');
            elementProps.form.hookFormSetValue('external_dataset_column_value_label', '');
            elementProps.form.hookFormSetValue('external_dataset_column_title_label', '');
        }
    }, [selectedDatasetId, elementProps.form]);

    return (
        <Stack style={{ position: 'relative' }} height={1}>
            <Box flexShrink={0}>
                <BlockEditPanelHeader
                    title={title}
                    onCancel={() => onCancel()}
                    item={field.column_name ?? field.reference_name}
                />
            </Box>
            <Stack sx={{ overflow: 'auto', flexGrow: 1, p: 3 }} spacing={2}>
                {/*ToDo Show labels as on design*/}
                <TemplateFormComponent
                    elementProps={elementProps}
                    component={'FormText'}
                    name={'filter_label'}
                    props={{ label: 'label' }}
                />

                <Box>
                    <FormLabel>{t('manage_filters.label_filter_type')}</FormLabel>
                    <TemplateFormComponent
                        elementProps={{ ...elementProps }}
                        componentValues={filterTypes
                            .filter((f) => f.valueType == valueType)
                            .map((f) => ({ label: t('manage_filters.' + f.label), value: f.type }))}
                        component={'FormSelect'}
                        name={'filterType'}
                    />
                </Box>

                <span className={fieldType == 'dropdown' || fieldType == 'multiselect' ? '' : 'd-none'}>
                    <FormLabel>{t('sort')}</FormLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        componentValues={sortOrderDirections
                            .filter((f) => f.valueType == valueType)
                            .map((f) => ({ label: t('manage_filters.' + f.label), value: f.order }))}
                        component={'FormSelect'}
                        name={'sortDirection'}
                    />
                </span>

                <span className={fieldType == 'dropdown' ? '' : 'd-none'}>
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'include_all_value'}
                        label={t('manage_filters.include_all_value')}
                    />
                </span>

                <span className={valueType == 'datetime' && fieldType == 'range' ? '' : 'd-none'}>
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'show_date_only'}
                        label={t('manage_filters.show_date_only')}
                    />
                </span>

                <EditPanelCheckBox
                    elementProps={elementProps}
                    name={'use_external_filter_value'}
                    label={t('manage_filters.use_external_filter_value')}
                />

                {useExternalFilterValue === 'Y' && (
                    <>
                        {(isDatasetListLoading || isColumnsListLoading) && (
                            <LoadingPlaceholder relativeToParent={true} />
                        )}
                        <Box>
                            <FormLabel>{t('manage_filters.external_dataset')}</FormLabel>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                componentValues={datasetsList}
                                component={'FormSelect'}
                                name={'external_dataset_id'}
                            />
                        </Box>
                        {selectedDatasetId && (
                            <Stack direction={'row'} spacing={2}>
                                <Box flex={1}>
                                    <FormLabel>{t('manage_filters.value_column')}</FormLabel>
                                    <TemplateFormComponent
                                        elementProps={elementProps}
                                        componentValues={columnsList}
                                        component={'FormSelect'}
                                        name={'external_dataset_column_value_id'}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <FormLabel>{t('manage_filters.title_column')}</FormLabel>
                                    <TemplateFormComponent
                                        elementProps={elementProps}
                                        componentValues={columnsList}
                                        component={'FormSelect'}
                                        name={'external_dataset_column_title_id'}
                                    />
                                </Box>
                            </Stack>
                        )}
                        <Box>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                component={'FormText'}
                                name={'external_delimiter'}
                                label={t('manage_filters.external_delimiter')}
                                props={{}}
                            />
                        </Box>
                    </>
                )}
            </Stack>
            <Box flexShrink={0}>
                <BlockEditPanelControls
                    onApply={() => {
                        const titleId = elementProps.form.hookFormGetValues('external_dataset_column_title_id') ?? '';
                        const valueId = elementProps.form.hookFormGetValues('external_dataset_column_value_id') ?? '';
                        onApply({
                            ...field,
                            filter_label: elementProps.form.hookFormGetValues('filter_label'),
                            include_all_value: elementProps.form.hookFormGetValues('include_all_value'),
                            show_date_only: elementProps.form.hookFormGetValues('show_date_only'),
                            filterType: elementProps.form.hookFormGetValues('filterType'),
                            sortDirection: elementProps.form.hookFormGetValues('sortDirection'),
                            use_external_filter_value: elementProps.form.hookFormGetValues('use_external_filter_value'),
                            external_dataset_id: elementProps.form.hookFormGetValues('external_dataset_id')
                                ? Number(elementProps.form.hookFormGetValues('external_dataset_id'))
                                : null,
                            external_dataset_column_value_id: valueId,
                            external_dataset_column_value_label:
                                columnsList.find((v) => v.value == valueId)?.label ?? '',
                            external_dataset_column_title_id: titleId,
                            external_dataset_column_title_label:
                                columnsList.find((v) => v.value == titleId)?.label ?? '',
                            external_delimiter: elementProps.form.hookFormGetValues('external_delimiter') || null,
                        });
                    }}
                    onCancel={() => onCancel()}
                    isTypeSave
                />
            </Box>
        </Stack>
    );
}
